import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomFieldsRenderer } from "./CustomFieldsRenderer";
import { LeadFormFields } from "./LeadFormFields";

// Stage Change Dialog
export function StageChangeDialog({
  open,
  onOpenChange,
  pendingStage,
  stageChangeNote,
  setStageChangeNote,
  saving,
  onConfirm
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle>Change Lead Stage</DialogTitle>
          <DialogDescription>
            Move this lead to "{pendingStage?.label}"?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label>Add a note (optional)</Label>
          <Textarea
            value={stageChangeNote}
            onChange={(e) => setStageChangeNote(e.target.value)}
            placeholder="Reason for stage change..."
            className="mt-2 min-h-20"
          />
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-11 sm:h-10">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={saving} className="w-full sm:w-auto h-11 sm:h-10">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Lead Dialog
export function EditLeadDialog({
  open,
  onOpenChange,
  editForm,
  setEditForm,
  saving,
  onSave,
  customValues = {},
  onCustomValueChange
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <LeadFormFields
            data={editForm}
            onChange={setEditForm}
            mode="edit"
            customFieldValues={customValues}
            onCustomFieldChange={onCustomValueChange}
            hideMeeting
          />
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-11 sm:h-10">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="w-full sm:w-auto h-11 sm:h-10">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add Task Dialog
export function AddTaskDialog({
  open,
  onOpenChange,
  newTask,
  setNewTask,
  onSave
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle>Add Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Task Title</Label>
            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="Enter task title"
              className="mt-1 h-11 sm:h-10"
            />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input
              type="date"
              value={newTask.due_date}
              onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
              className="mt-1 h-11 sm:h-10"
            />
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={newTask.priority} onValueChange={(val) => setNewTask({ ...newTask, priority: val })}>
              <SelectTrigger className="mt-1 h-11 sm:h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-11 sm:h-10">
            Cancel
          </Button>
          <Button onClick={onSave} className="w-full sm:w-auto h-11 sm:h-10">
            Add Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add Meeting Dialog
export function AddMeetingDialog({
  open,
  onOpenChange,
  newMeeting,
  setNewMeeting,
  onSave
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle>Schedule Meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Meeting Title</Label>
            <Input
              value={newMeeting.title}
              onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
              placeholder="Enter meeting title"
              className="mt-1 h-11 sm:h-10"
            />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={newMeeting.date}
              onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
              className="mt-1 h-11 sm:h-10"
            />
          </div>
          <div>
            <Label>Time</Label>
            <Input
              type="time"
              value={newMeeting.time}
              onChange={(e) => setNewMeeting({ ...newMeeting, time: e.target.value })}
              className="mt-1 h-11 sm:h-10"
            />
          </div>
          <div>
            <Label>Duration (minutes)</Label>
            <Select value={newMeeting.duration} onValueChange={(val) => setNewMeeting({ ...newMeeting, duration: val })}>
              <SelectTrigger className="mt-1 h-11 sm:h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-11 sm:h-10">
            Cancel
          </Button>
          <Button onClick={onSave} className="w-full sm:w-auto h-11 sm:h-10">
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
